import React from "react";
import Slider from "react-slick";
import get from "lodash/get";
import TimeAgo from "react-timeago";
import { LazyLoadImages, Link, ResponsiveHeroImage, ReviewRating } from "@quintype/components";
import { ImageFallback } from "./image-fallback.js";
import { Author } from "./author.js";
import { DotIcon } from "./dot-icon.js";
import { getNumberOfStoriesToShow, altTagOfHeroImages, isExternalStory } from "../../utils";
import { formatter, getCarousel } from "../../utils";

function Carousel(props) {
  const {
    associatedMetadata: { slider_type_dots: sliderTypeDots = false, slider_type_dashes: sliderTypeDashes = false } = {}
  } = props;

  const sliderPropObject = {
    dots: sliderTypeDashes || sliderTypeDots,
    speed: props.autoplaySpeed,
    rtl: get(props.config, ["theme-attributes", "language_direction"]) === "right",
    arrows: props.arrows,
    dotsClass: sliderTypeDashes ? "dashpaging" : "slick-dots",
    numberOfStoriesToShow: props.numberOfStoriesToShow,
    lazyLoad: true,
    onLazyLoad: () => {}
  };

  const SliderWrapper = (
    <Slider {...sliderPropObject}>
      {props.stories.slice(0, props.numberOfStoriesToShow).map(storyObj => {
        const story = storyObj && storyObj.story ? storyObj.story : storyObj;
        return (
          <div key={story.id}>
            {React.createElement(props.itemTemplate || CarouselItem, {
              story: story,
              config: props.config,
              associatedMetadata: props.associatedMetadata,
              collection: props.collection
            })}
          </div>
        );
      })}
    </Slider>
  );

  const Slide = React.createElement(props.itemTemplate || CarouselItem, {
    story: props.stories[0],
    config: props.config,
    associatedMetadata: props.associatedMetadata,
    collection: props.collection
  });

  if (!props.stories) return null;

  return (
    <div className="carousel-wrapper">
      <div
        className={`carousel slider ${
          get(props.config, ["theme-attributes", "language_direction"]) === "right" ? "dots-rtl" : ""
        }`}
      >
        {getCarousel(SliderWrapper, Slide)}
      </div>
    </div>
  );
}

Carousel.defaultProps = {
  dots: true,
  infinite: true,
  speed: 500,
  arrows: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: false,
  autoplaySpeed: 500,
  dotsClass: "dashpaging",
  collectionName: true,
  numberOfStoriesToShow: 6
};

function CarouselItem(props) {
  const { story, collection, config = {}, associatedMetadata = {} } = props;
  if (!story) return null;
  return (
    <Link href={"/" + story.slug} externalLink={isExternalStory(story)}>
      <Slide associatedMetadata={associatedMetadata} story={story} config={config} collection={collection} />
    </Link>
  );
}

function authorImageUrl(author) {
  if (!author || !author["avatar-url"]) return;

  // FIXME: Hack. Better to use FocusImage class from Quintype-JS
  if (author["avatar-s3-key"] && author["avatar-s3-key"] !== "") {
    return `${author["avatar-url"]}?w=96&h=96`;
  }

  return author["avatar-url"];
}

function Slide(props) {
  const {
    associatedMetadata: { show_section_tag = true, show_author_name = false, show_time_of_publish = true },
    story: { "story-template": storyTemplate },
    collection: { collection } = {}
  } = props;
  const isReviewStory = props.story["story-template"] === "review";
  const publishedTime = props.story["last-published-at"]
    ? props.story["last-published-at"]
    : props.story["first-published-at"];
  // const storyTemplate = props.story["story-template"];
  // const storyTemplate = props.story["story-template"];
  return (
    <div>
      <div
        className={`carousel-slide ${
          get(props.config, ["theme-attributes", "language_direction"]) === "right" ? "rtl-section-header" : ""
        }`}
      >
        <div className="carousel-slide__image">
          {props.story["hero-image-s3-key"] ? (
            <figure className={`carousel-slide__image__${storyTemplate}`}>
              <LazyLoadImages margin={"0px"}>
                <ResponsiveHeroImage
                  story={props.story}
                  aspectRatio={[4, 3]}
                  alt={altTagOfHeroImages(props.story)}
                  defaultWidth={480}
                  widths={[250, 480, 640]}
                  sizes="(max-width: 500px) 98%, (max-width: 768px) 48%, 23%"
                  imgParams={{ auto: ["format", "compress"], fit: "max" }}
                />
              </LazyLoadImages>
            </figure>
          ) : (
            <ImageFallback />
          )}
        </div>
        <div className="carousel-slide__content">
          {show_section_tag ? (
            <div className="carousel-slide__section" data-testid="carousel-sectionname">
              {get(props.story, ["sections", 0, "display-name"]) || get(props.story, ["sections", 0, "name"], "")}
            </div>
          ) : null}
          <h2 className="carousel-slide__heading" data-test-id="carousel-story-headline">
            <bdi dangerouslySetInnerHTML={{ __html: props.story.headline }} />
          </h2>
          {isReviewStory && <ReviewRating value={get(props.story, ["metadata", "review-rating", "value"], "0")} />}
          <p className="carousel-slide__description">
            <bdi dangerouslySetInnerHTML={{ __html: props.story.subheadline }} />
          </p>
          <div className="post-details">
            {show_author_name ? (
              <div data-testid="carousel-authorname">
                <Author
                  author={Object.assign({}, get(props.story, ["authors", 0]), {
                    name: get(props.story, ["authors", 0, "name"], props.story["author-name"]),
                    image: authorImageUrl(get(props.story, ["authors", 0])),
                    id: get(props.story, ["authors", 0, "id"], props.story["author-id"])
                  })}
                  config={props.config}
                />
              </div>
            ) : null}
            {show_author_name && show_time_of_publish ? <DotIcon fill="#858585" /> : null}
            {show_time_of_publish ? (
              <div className="post-time" data-testid="carousel-showtime">
                <TimeAgo
                  date={publishedTime}
                  formatter={(value, unit, suffix, date) => formatter(value, unit, suffix, date, props.config)}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export { Carousel };
